/**
 * sc-48721: riquadro in fondo al PDF del preventivo, impostato come un conto.
 * Condiviso da QuotePage, EditQuotePage e CreateQuotePage.
 */
import { daRestituire } from "./pagamenti";

// Il meno è un trattino medio (U+2013): il segno matematico «−» (U+2212)
// non è nei font standard di jsPDF e uscirebbe come un carattere a caso.
export const MENO = "–";

// Etichetta e valore su due righe: la riga unica «Intestato a: …» era larga
// 83 mm e stringeva la colonna del riepilogo.
export const DATI_BANCARI = [
  { label: "IBAN", value: "IT47B0871384630000000413935" },
  { label: "Intestato a", value: "EMMA MAISON DI MORALE CORRADO" },
];

const HEAD_PAD_TOP = 10;
const LINE_H = 6;
const RULE_H = 3;
const COL_PAD_X = 6; // margine interno di ogni colonna
// Griglia fissa: Riepilogo | Dettagli pagamento | Firma (quote della
// larghezza utile). La colonna centrale deve contenere il valore bancario
// più lungo (~58 mm a 9 pt) più i margini.
const QUOTE_COLONNE = [0.4, 0.38];
const SEPARATORE_PAD_Y = 5; // stacco dei separatori dai bordi del riquadro
const SIG_MIN_GAP = 12; // spazio minimo fra titolo e linea della firma
const MIN_BLOCK_H = 40;
const BANCA_LABEL_PT = 8;
const BANCA_VALUE_PT = 9;
const BANCA_LABEL_TO_VALUE = 4.5; // dall'etichetta al suo valore
const BANCA_VALUE_TO_LABEL = 6.5; // dal valore all'etichetta successiva

const cent = (n) => Number((Number(n) || 0).toFixed(2));

/** Imponibile delle righe tabella: gli importi sono IVA inclusa. */
export function totaliImponibile(righe = []) {
  return cent(
    righe.reduce((tot, r) => {
      const importo = Number(r?.importo) || 0;
      const iva = Number(r?.iva) || 0;
      return tot + importo / (1 + iva / 100);
    }, 0)
  );
}

export function calcolaImporti(quote = {}) {
  const totaleArticoli = cent(quote.totalPrice);
  const percent = Number(quote.discount_percent) || 0;
  const scontoSalvato = cent(quote.discount);
  // Lo sconto salvato come importo vince; la percentuale è per i preventivi
  // precedenti a sc-46947.
  const sconto =
    scontoSalvato > 0 ? scontoSalvato : cent((totaleArticoli * percent) / 100);
  const totalePreventivo = Math.max(0, cent(totaleArticoli - sconto));
  const acconto = cent(quote.deposit);
  return {
    totaleArticoli,
    sconto,
    percent,
    totalePreventivo,
    acconto,
    daPagare: Math.max(0, cent(totalePreventivo - acconto)),
    credito: daRestituire(totalePreventivo - acconto),
  };
}

export function righeRiepilogo(importi, imponibile) {
  // Imponibile e IVA sono il dettaglio del totale, non addendi: grigi e
  // senza segno. L'IVA è la differenza, così i due sommano sempre al totale.
  const righe = [
    { label: "Imponibile", amount: cent(imponibile), tone: "muted" },
    {
      label: "IVA",
      amount: cent(importi.totaleArticoli - imponibile),
      tone: "muted",
    },
  ];
  // Senza sconto «Totale articoli» coinciderebbe con «Totale preventivo».
  if (importi.sconto > 0) {
    righe.push({ label: "Totale articoli", amount: importi.totaleArticoli });
    const pct = importi.percent ? ` (${importi.percent}%)` : "";
    righe.push({ label: `Sconto${pct}`, amount: importi.sconto, sign: MENO });
  }
  righe.push({ rule: true });
  righe.push({
    label: "Totale preventivo",
    amount: importi.totalePreventivo,
    tone: "strong",
  });
  if (importi.acconto > 0) {
    righe.push({ label: "Acconto", amount: importi.acconto, sign: MENO });
    righe.push({ rule: true });
    righe.push(
      importi.credito > 0
        ? {
            label: "Da restituire al cliente",
            amount: importi.credito,
            tone: "strong",
          }
        : {
            label: "Totale da pagare",
            amount: importi.daPagare,
            tone: "strong",
          }
    );
  }
  return righe;
}

function fontDi(doc, row) {
  if (row.tone === "strong") doc.setFont("helvetica", "bold").setFontSize(10.5);
  else if (row.tone === "muted")
    doc.setFont("helvetica", "normal").setFontSize(9);
  else doc.setFont("helvetica", "normal").setFontSize(10);
}

const importoDi = (row) =>
  `${row.sign ? `${row.sign} ` : ""}€ ${Number(row.amount).toFixed(2)}`;

/**
 * Disegna Riepilogo | Dettagli pagamento | Firma e restituisce la y sotto
 * il riquadro. Il salto pagina resta a carico del chiamante.
 */
export function disegnaRiquadroRiepilogo(
  doc,
  { y, pageWidth, marginX, righe }
) {
  const mm = (n) => Math.round(n * 10) / 10;
  const larghezza = pageWidth - 2 * marginX;
  const bordo1 = mm(marginX + larghezza * QUOTE_COLONNE[0]);
  const bordo2 = mm(bordo1 + larghezza * QUOTE_COLONNE[1]);
  const col1X = marginX + COL_PAD_X;
  const col1Right = bordo1 - COL_PAD_X;
  const col2X = bordo1 + COL_PAD_X;
  const col3X = bordo2 + COL_PAD_X;
  const sigEnd = pageWidth - marginX - COL_PAD_X;

  // Misurare prima di disegnare: una voce lunga va a capo, l'importo resta
  // sulla sua prima riga. La larghezza dipende dal font: va impostato prima.
  const layout = righe.map((row) => {
    if (row.rule) return { row, h: RULE_H };
    fontDi(doc, row);
    const spazio = col1Right - col1X - doc.getTextWidth(importoDi(row)) - 3;
    const testo = doc.splitTextToSize(row.label, spazio);
    return { row, testo, h: testo.length * LINE_H };
  });
  const corpo = layout.reduce((tot, l) => tot + l.h, 0);
  // Sotto l'ultima riga di testo restano 8 mm (non serve l'interlinea per
  // una riga successiva). Si toglie una riga sola, non l'altezza dell'ultima
  // voce: se va a capo, anche la sua seconda riga deve stare nel riquadro.
  const leftHeight = HEAD_PAD_TOP + 7 + corpo - LINE_H + 8;
  const corpoCentro =
    DATI_BANCARI.length * BANCA_LABEL_TO_VALUE +
    (DATI_BANCARI.length - 1) * BANCA_VALUE_TO_LABEL;
  const centerHeight = HEAD_PAD_TOP + 7 + corpoCentro + 8;
  const rightHeight = HEAD_PAD_TOP + 2 + 7 + SIG_MIN_GAP + 8;
  const blockH = Math.max(MIN_BLOCK_H, leftHeight, centerHeight, rightHeight);

  doc.setFillColor(240, 240, 240);
  doc.rect(marginX, y, larghezza, blockH, "F");

  // Separatori verticali fra le colonne
  doc.setDrawColor(200);
  [bordo1, bordo2].forEach((x) => {
    doc.line(x, y + SEPARATORE_PAD_Y, x, y + blockH - SEPARATORE_PAD_Y);
  });

  // Titoli sulla stessa riga, ciascuno a sinistra della sua colonna
  const titoloY = y + HEAD_PAD_TOP;
  doc.setTextColor(0);
  doc.setFont("helvetica", "bold").setFontSize(12);
  doc.text("Riepilogo", col1X, titoloY);
  doc.text("Dettagli pagamento", col2X, titoloY);
  doc.text("Firma", col3X, titoloY);

  // Colonna 1: il conto
  let fy = titoloY + 7;
  layout.forEach(({ row, testo, h }) => {
    if (row.rule) {
      // la linea sta a metà dello spazio che le è riservato
      const ly = fy - LINE_H / 2 + RULE_H / 2;
      doc.setDrawColor(170);
      doc.line(col1X, ly, col1Right, ly);
      fy += h;
      return;
    }
    fontDi(doc, row);
    doc.setTextColor(row.tone === "muted" ? 110 : 0);
    doc.text(testo, col1X, fy);
    doc.text(importoDi(row), col1Right, fy, { align: "right" });
    fy += h;
  });

  // Colonna 2: dati bancari, etichetta grigia sopra e valore sotto
  let fb = titoloY + 7;
  DATI_BANCARI.forEach(({ label, value }, i) => {
    if (i > 0) fb += BANCA_VALUE_TO_LABEL;
    doc.setFont("helvetica", "normal").setFontSize(BANCA_LABEL_PT);
    doc.setTextColor(110);
    doc.text(label, col2X, fb);
    fb += BANCA_LABEL_TO_VALUE;
    doc.setFontSize(BANCA_VALUE_PT);
    doc.setTextColor(0);
    doc.text(value, col2X, fb);
  });

  // Colonna 3: la linea della firma in fondo al riquadro, larga quanto la
  // colonna
  const sigLineY = y + blockH - 8;
  doc.setDrawColor(0);
  doc.line(col3X, sigLineY, sigEnd, sigLineY);

  // colori di default per chi disegna dopo
  doc.setTextColor(0);
  doc.setDrawColor(0);

  return y + blockH + 5;
}
